// Distance helpers for location-ranked V2 content (home carousels).
// Kept framework-free so they can be unit-tested and reused by any screen.

const EARTH_RADIUS_M = 6371000;

const toRad = (deg) => (deg * Math.PI) / 180;

// Number("") and Number(null) are both 0, which would silently place a club with
// blank coordinates on the equator and report a ~5,000 km distance instead of
// "unknown". Anything that is not a real number reads as NaN so the finite check
// below rejects it. Numeric 0 stays valid.
export const toCoord = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === "string" && value.trim() === "") return NaN;
  return Number(value);
};

/**
 * Great-circle distance in metres between two {latitude, longitude} points.
 * Returns null when either point is incomplete — carousel items and the saved
 * client address can both arrive without coordinates.
 *
 * Haversine (spherical earth) is ~0.5% off at Israel scale, two orders of
 * magnitude below the resolution of a label rounded to the nearest 10 m.
 *
 * @param {{latitude: number, longitude: number}|null} from
 * @param {{latitude: number, longitude: number}|null} to
 * @returns {number|null} metres, or null if unknown
 */
export const distanceInMeters = (from, to) => {
  const lat1 = toCoord(from?.latitude);
  const lon1 = toCoord(from?.longitude);
  const lat2 = toCoord(to?.latitude);
  const lon2 = toCoord(to?.longitude);
  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return null;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(a)));
};

/**
 * "890 מטר" below 1 km, "1.2 ק"מ" above. Returns null when the distance is
 * unknown so the caller can drop the element entirely rather than render a gap.
 *
 * Metres are rounded to 10 — a low-accuracy fix is ±20 m, so "887 מטר" would be
 * false precision.
 *
 * @param {number|null} meters
 * @param {Function} t - i18next translate
 * @returns {string|null}
 */
export const formatDistance = (meters, t) => {
  if (meters == null) return null;
  if (meters < 1000) return t("DISTANCE_METERS", { value: Math.round(meters / 10) * 10 });
  return t("DISTANCE_KM", { value: (meters / 1000).toFixed(1) });
};
